import { Box, Tab, Tabs, useTheme } from '@mui/material'
import TabMeasureIcon from './TabMeasureIcon'
import React, { useEffect, useState } from 'react'
import { MeasureDataContext } from '../contexts/MeasureDataProvider'
import { AppContext } from '../contexts/AppProvider'
import { useAppNavigate } from '../hooks/useAppNavigate'
import { ROUTES } from '../routes/routes'

const MeasureTabsBar: React.FC = () => {
  const { navigateToConverter } = useAppNavigate()
  const theme = useTheme()

  const { allMeasures } = React.useContext(MeasureDataContext)
  const allMeasuresArr = Object.values(allMeasures)
  const { appState } = React.useContext(AppContext)
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(allMeasuresArr.indexOf(appState.currentMeasure))
  const [selectedTabColor, setSelectedTabColor] = useState(theme.palette.custom.solidBeauty)

  useEffect(() => {
    const newMeasureIndex = allMeasuresArr.indexOf(appState.currentMeasure)
    setCurrentTabIndex(newMeasureIndex)

    updateTabColor(newMeasureIndex)
  }, [appState.currentMeasure])

  useEffect(() => {
    // code to make vertical scroll be horizontal scroll on tabs, ref didn't work.
    const tabsScroller = document.querySelector('.MuiTabs-scroller')

    const handleWheel = (event: WheelEvent): void => {
      if (event.deltaY !== 0) {
        event.preventDefault()
        if (tabsScroller != null) {
          tabsScroller.scrollLeft += event.deltaY
        }
      }
    }

    tabsScroller?.addEventListener('wheel', handleWheel)

    return () => {
      tabsScroller?.removeEventListener('wheel', handleWheel)
    }
  }, [])

  const interpolateTabColor = (factor: number): string => {
    const color1 = theme.palette.custom.solidBeauty
    const color2 = '#00aeef'
    const parseHex = (hex: string): number => parseInt(hex, 16)

    const r1 = parseHex(color1.slice(1, 3))
    const g1 = parseHex(color1.slice(3, 5))
    const b1 = parseHex(color1.slice(5, 7))

    const r2 = parseHex(color2.slice(1, 3))
    const g2 = parseHex(color2.slice(3, 5))
    const b2 = parseHex(color2.slice(5, 7))

    const r = Math.round(r1 + factor * (r2 - r1))
    const g = Math.round(g1 + factor * (g2 - g1))
    const b = Math.round(b1 + factor * (b2 - b1))

    const toHex = (n: number): string => n.toString(16).padStart(2, '0')

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  const updateTabColor = (tabIndex: number): void => {
    const colorFactor = tabIndex / (allMeasuresArr.length - 1)
    const newTabColor = interpolateTabColor(colorFactor)
    setSelectedTabColor(newTabColor)
  }

  const handleMeasureChange = (event: React.SyntheticEvent, newMeasureIndex: number): void => {
    const newCurrentMeasure = allMeasuresArr[newMeasureIndex]

    updateTabColor(newMeasureIndex)

    navigateToConverter(
      newCurrentMeasure,
      appState.tabs[newCurrentMeasure.codeName].fromUnit,
      appState.tabs[newCurrentMeasure.codeName].toUnit
    )
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent="center" >
      <Tabs
        value={currentTabIndex}
        onChange={handleMeasureChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="scrollable list of measures (e.g. length, temperature)"
        TabIndicatorProps={{
          style: {
            backgroundColor: selectedTabColor
          }
        }}
      >
        {allMeasuresArr.map((m, index) => (
            <Tab
              component="a"
              href={ROUTES.MEASURE.create(m)}
              onClick={(e) => {
                // Prevent default link navigation
                e.preventDefault()
                // Tabs component will call handleMeasureChange with the right index
              }}
              icon={<TabMeasureIcon measure={m} />}
              iconPosition="start"
              key={index}
              label={m.displayName}
              aria-current={currentTabIndex === index ? 'page' : undefined}
              aria-label={`Switch to ${m.displayName} converter`}
              sx={{
                fontWeight: 300,
                '&.Mui-selected': {
                  color: selectedTabColor
                },
                color: `${theme.palette.grey[500]}`
              }}
            />
          )
        )}
      </Tabs>
    </Box>
  )
}

export default MeasureTabsBar
